import React, { useState } from 'react';
import { useAuth } from '../../components/Auth/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Settings, Mail, Lock, Bell, Shield, Trash2, AlertTriangle } from 'lucide-react';

export default function AccountSettingsModal({ open, onClose, user }) {
  const { updateUserProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('account');
  
  // Account settings state
  const [emailData, setEmailData] = useState({
    currentEmail: user?.email || '',
    newEmail: '',
    confirmEmail: '',
    password: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleEmailChange = async () => {
    if (emailData.newEmail !== emailData.confirmEmail) {
      alert('Les adresses email ne correspondent pas');
      return;
    }

    setLoading(true);
    try {
      // TODO: Implement email change API call
      alert('Fonctionnalité de changement d\'email à implémenter');
    } catch (error) {
      alert('Erreur lors du changement d\'email: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('Les mots de passe ne correspondent pas');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      alert('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }

    setLoading(true);
    try {
      // TODO: Implement password change API call  
      alert('Fonctionnalité de changement de mot de passe à implémenter');
    } catch (error) {
      alert('Erreur lors du changement de mot de passe: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetEmailForm = () => {
    setEmailData({
      currentEmail: user?.email || '',
      newEmail: '',
      confirmEmail: '',
      password: ''
    });
  };

  const resetPasswordForm = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleClose = () => {
    resetEmailForm();
    resetPasswordForm();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Paramètres du compte
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="account" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Compte
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              Sécurité
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="privacy" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Confidentialité
            </TabsTrigger>
          </TabsList>

          <div className="mt-6 max-h-96 overflow-y-auto">
            <TabsContent value="account" className="space-y-6">
              {/* Email Change Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Adresse email</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Email actuel</Label>
                    <Input
                      value={emailData.currentEmail}
                      disabled
                      className="bg-gray-50"
                    />
                  </div>
                  
                  <div>
                    <Label>Nouvelle adresse email</Label>
                    <Input
                      type="email"
                      value={emailData.newEmail}
                      onChange={(e) => setEmailData(prev => ({ ...prev, newEmail: e.target.value }))}
                      placeholder="nouvelle@email.com"
                    />
                  </div>
                  
                  <div>
                    <Label>Confirmer la nouvelle adresse</Label>
                    <Input
                      type="email"
                      value={emailData.confirmEmail}
                      onChange={(e) => setEmailData(prev => ({ ...prev, confirmEmail: e.target.value }))}
                      placeholder="nouvelle@email.com"
                    />
                  </div>
                  
                  <div>
                    <Label>Mot de passe actuel (pour confirmation)</Label>
                    <Input
                      type="password"
                      value={emailData.password}
                      onChange={(e) => setEmailData(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Votre mot de passe actuel"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={resetEmailForm}>
                      Annuler
                    </Button>
                    <Button 
                      onClick={handleEmailChange} 
                      disabled={loading || !emailData.newEmail || !emailData.confirmEmail || !emailData.password}
                    >
                      {loading ? 'Modification...' : 'Modifier l\'email'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="security" className="space-y-6">
              {/* Password Change Section */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Mot de passe</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Mot de passe actuel</Label>
                    <Input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      placeholder="Votre mot de passe actuel"
                    />
                  </div>
                  
                  <div>
                    <Label>Nouveau mot de passe</Label>
                    <Input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                      placeholder="Au moins 6 caractères"
                    />
                  </div>
                  
                  <div>
                    <Label>Confirmer le nouveau mot de passe</Label>
                    <Input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      placeholder="Répétez le nouveau mot de passe"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={resetPasswordForm}>
                      Annuler
                    </Button>
                    <Button 
                      onClick={handlePasswordChange}
                      disabled={loading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                    >
                      {loading ? 'Modification...' : 'Modifier le mot de passe'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Two-Factor Authentication - Future */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Authentification à deux facteurs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">
                      Fonctionnalité à venir pour renforcer la sécurité de votre compte
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="notifications" className="space-y-6">
              {/* Notifications Settings - Future */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Préférences de notifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">
                      Paramètres de notifications à venir
                    </p>
                    <p className="text-gray-400 text-sm mt-2">
                      Email, push, likes, commentaires, etc.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="privacy" className="space-y-6">
              {/* Privacy Settings - Future */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Confidentialité et données</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6">
                    <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">
                      Paramètres de confidentialité à venir
                    </p>
                    <p className="text-gray-400 text-sm mt-2">
                      Gestion des données, téléchargement, suppression
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Account Deletion - Future */}
              <Card className="border-red-200">
                <CardHeader>
                  <CardTitle className="text-lg text-red-600">Zone de danger</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4 p-4 bg-red-50 rounded-lg border border-red-200">
                    <AlertTriangle className="h-8 w-8 text-red-500 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="font-medium text-red-800">Supprimer le compte</h4>
                      <p className="text-red-700 text-sm">
                        Suppression définitive de toutes vos données
                      </p>
                    </div>
                    <Button variant="destructive" disabled>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Supprimer
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>

        <div className="flex justify-end pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}